import React, { useState, useEffect } from 'react';
import { getFirestore, collection, getDocs, query, where, getDoc, doc } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import ContactListTable from './ContactListTable';

export default function ContactManagementView() {
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [jobsMap, setJobsMap] = useState({});

  // Fetch all contacts
  useEffect(() => {
    fetchContacts();
  }, []);

  // Filter contacts based on search term
  useEffect(() => {
    if (!contacts.length) {
      setFilteredContacts([]);
      return;
    }

    let filtered = [...contacts];

    // Apply search filter if there's a search term
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(contact => {
        return (
          (contact.firstName && contact.firstName.toLowerCase().includes(lowerSearchTerm)) ||
          (contact.lastName && contact.lastName.toLowerCase().includes(lowerSearchTerm)) ||
          (contact.email && contact.email.toLowerCase().includes(lowerSearchTerm)) ||
          (contact.company && contact.company.toLowerCase().includes(lowerSearchTerm)) ||
          (contact.jobTitle && contact.jobTitle.toLowerCase().includes(lowerSearchTerm))
        );
      });
    }

    // Sort the filtered contacts
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle timestamps
      if (aValue && aValue.seconds) {
        aValue = new Date(aValue.seconds * 1000);
      }
      if (bValue && bValue.seconds) {
        bValue = new Date(bValue.seconds * 1000);
      }

      // Handle undefined or null values
      if (aValue === undefined || aValue === null) return sortDirection === 'asc' ? -1 : 1;
      if (bValue === undefined || bValue === null) return sortDirection === 'asc' ? 1 : -1;

      // Compare based on type
      if (typeof aValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      } else {
        const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        return sortDirection === 'asc' ? comparison : -comparison;
      }
    });

    setFilteredContacts(filtered);
  }, [contacts, searchTerm, sortField, sortDirection]);

  const fetchContacts = async () => {
    setLoadingContacts(true);
    try {
      const db = getFirestore();
      
      // Get all contacts
      const contactsRef = collection(db, 'contacts');
      const contactsSnapshot = await getDocs(contactsRef);
      
      // Group contacts by email to handle multiple jobs per contact
      const contactsMap = {};
      const jobIds = new Set();
      
      contactsSnapshot.forEach((doc) => {
        const contactData = {
          id: doc.id,
          ...doc.data()
        };
        
        // Add job ID to the set of job IDs to fetch
        if (contactData.jobId) {
          jobIds.add(contactData.jobId);
        }
        
        // Use email as the key for grouping contacts
        const email = contactData.email?.toLowerCase() || 'unknown';
        
        if (!contactsMap[email]) {
          // Create a new contact entry with jobs array
          contactsMap[email] = {
            ...contactData,
            jobs: contactData.jobId ? [contactData.jobId] : [],
            // Keep track of all contact IDs associated with this email
            contactIds: [contactData.id]
          };
        } else {
          // Update existing contact with additional job
          if (contactData.jobId && !contactsMap[email].jobs.includes(contactData.jobId)) {
            contactsMap[email].jobs.push(contactData.jobId);
          }
          contactsMap[email].contactIds.push(contactData.id);
          
          // Use the most recent contact data for fields
          const existingTimestamp = contactsMap[email].createdAt?.seconds || 0;
          const newTimestamp = contactData.createdAt?.seconds || 0;
          
          if (newTimestamp > existingTimestamp) {
            // Keep the jobs array and contactIds, but update other fields
            const jobs = [...contactsMap[email].jobs];
            const contactIds = [...contactsMap[email].contactIds];
            contactsMap[email] = {
              ...contactData,
              jobs,
              contactIds
            };
          }
        }
      });
      
      // Convert map to array
      const contactsData = Object.values(contactsMap);
      
      // Fetch job details for all job IDs
      const jobsData = {};
      
      for (const jobId of jobIds) {
        try {
          const jobDoc = await getDoc(doc(db, 'jobs', jobId));
          if (jobDoc.exists()) {
            jobsData[jobId] = {
              id: jobId,
              ...jobDoc.data()
            };
          }
        } catch (error) {
          console.error(`Error fetching job ${jobId}:`, error);
        }
      }
      
      setJobsMap(jobsData);
      setContacts(contactsData);
    } catch (error) {
      console.error("Error fetching contacts:", error);
      toast.error(`Failed to load contacts: ${error.message}`);
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleSort = (field) => {
    // If clicking the same field, toggle direction
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new field, set it as the sort field with default direction
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="space-y-6">
      {/* Header with search */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center flex-wrap gap-4">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">Fast Track Contacts</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              All contacts added as fast track connections for jobs
            </p>
          </div>
          
          {/* Search input */}
          <div className="flex-1 max-w-md">
            <label htmlFor="search" className="sr-only">
              Search contacts
            </label>
            <div className="relative rounded-md shadow-sm">
              <input
                type="text"
                name="search"
                id="search"
                className="focus:ring-purple-500 focus:border-purple-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md"
                placeholder="Search contacts..."
                value={searchTerm}
                onChange={handleSearch}
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        {/* Active filters */}
        {searchTerm && (
          <div className="px-4 py-3 sm:px-6 bg-gray-50 border-t border-gray-200">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-500">Filters:</span>
              
              {/* Search Term Pill */}
              <span className="inline-flex items-center rounded-full bg-purple-100 py-1 pl-3 pr-1 text-sm font-medium text-purple-700 border border-purple-300">
                "{searchTerm}"
                <button
                  type="button"
                  onClick={clearSearch}
                  className="ml-1 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-purple-400 hover:bg-purple-200 hover:text-purple-500 focus:bg-purple-500 focus:text-white focus:outline-none"
                >
                  <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                  </svg>
                </button>
              </span>
            </div>
          </div>
        )}
        
        {/* Results count */}
        <div className="px-4 py-2 sm:px-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-500">
            {loadingContacts ? (
              <span>Loading contacts...</span>
            ) : (
              <span>
                Showing {filteredContacts.length} of {contacts.length} contacts
              </span>
            )}
          </div>
        </div>
        
        {/* Contact List Table */}
        <ContactListTable 
          contacts={filteredContacts}
          jobs={jobsMap}
          isLoading={loadingContacts}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
      </div>
    </div>
  );
}
